import React from 'react';
import { Button, Empty, Select, Table } from 'antd';
import { DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { fetchItemsByKeyword } from '../../../actions/items/application/fetch';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import { Item } from '../../../../types/item';
import { useAppDispatch } from '../../../store/ReduxStore';
import { typingDone } from '../../../utils/Utils';
import { DragHandle } from '../../global/dragableTable/DragHandle';
import { RowContext } from '../../global/dragableTable/DragRow';

type SelectedItem = {
  partNumber: string;
  brandCode: string;
  itemId: number;
  uniqueId: string; // Stable ID for drag-and-drop
};

type ProductRelationFieldProps = {
  value: string;
  productRelationDelimiter: string;
  productRelationBrandCodeDelimiter?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

// DraggableRow component for Ant Design Table
interface DraggableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const DraggableRow: React.FC<DraggableRowProps> = props => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  // Create context for DragHandle
  const contextValue = React.useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const ProductRelationField: React.FC<ProductRelationFieldProps> = ({
  value,
  productRelationDelimiter,
  productRelationBrandCodeDelimiter,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const searchedItems = useSelector(
    (state: ApplicationState) => state.items.application.applicationParentItems as Item[]
  );

  const selectedBrandId = useSelector(
    (state: ApplicationState) => state.parent.brands.selectedBrandId
  );

  const [selectedItems, setSelectedItems] = React.useState<SelectedItem[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [selectKey, setSelectKey] = React.useState<number>(0);
  const uniqueIdCounter = React.useRef<number>(0);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  // Parse value string into selected items
  const parseValue = React.useCallback(
    (val: string): SelectedItem[] => {
      if (!val) return [];

      const rows = val.split(productRelationDelimiter).filter(row => row.trim());
      return rows.map((row, index) => {
        const uniqueId = `item-${uniqueIdCounter.current++}`;
        if (productRelationBrandCodeDelimiter && row.includes(productRelationBrandCodeDelimiter)) {
          const [partNumber, brandCode] = row.split(productRelationBrandCodeDelimiter);
          return {
            partNumber: partNumber.trim(),
            brandCode: brandCode.trim(),
            itemId: index,
            uniqueId,
          };
        }
        return { partNumber: row.trim(), brandCode: '', itemId: index, uniqueId };
      });
    },
    [productRelationDelimiter, productRelationBrandCodeDelimiter]
  );

  // Reconstruct value string from selected items
  const reconstructValue = React.useCallback(
    (items: SelectedItem[]): string => {
      return items
        .map(item => {
          if (
            productRelationBrandCodeDelimiter != null &&
            productRelationBrandCodeDelimiter !== '' &&
            item.brandCode != null &&
            item.brandCode.trim() !== ''
          ) {
            return `${item.partNumber}${productRelationBrandCodeDelimiter}${item.brandCode}`;
          }
          return item.partNumber;
        })
        .join(productRelationDelimiter);
    },
    [productRelationDelimiter, productRelationBrandCodeDelimiter]
  );

  // Initialize selected items from value prop
  React.useEffect(() => {
    setSelectedItems(parseValue(value));
  }, [value, parseValue]);

  // Load items by keyword
  const loadItemsByKeyword = (keyword?: string) => {
    // Only search within current brand if no brand code delimiter is set
    const brandIdFilter =
      !productRelationBrandCodeDelimiter || productRelationBrandCodeDelimiter.trim() === ''
        ? selectedBrandId
        : null;

    return dispatch(
      fetchItemsByKeyword({
        brandId: brandIdFilter,
        selectedIds: [],
        keyword,
      })
    );
  };

  const handleSearch = (search: string) => {
    setSearchValue(search);
    typingDone(() => loadItemsByKeyword(search));
  };

  const handleSelect = (value: string | number | undefined) => {
    if (!value) return;
    const itemId = typeof value === 'string' ? parseInt(value, 10) : value;
    const item = searchedItems.find(i => i.id === itemId);
    if (!item) return;

    const newItem: SelectedItem = {
      partNumber: item.part_number,
      brandCode: item.brand_code,
      itemId: item.id,
      uniqueId: `item-${uniqueIdCounter.current++}`,
    };

    const newSelectedItems = [...selectedItems, newItem];
    setSelectedItems(newSelectedItems);
    onChange(reconstructValue(newSelectedItems));

    // Clear search results and search input after selection
    setSearchValue('');
    loadItemsByKeyword('');
    // Force Select to remount and clear internal state
    setSelectKey(prev => prev + 1);
  };

  const handleDelete = (index: number) => {
    const newSelectedItems = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(newSelectedItems);
    onChange(reconstructValue(newSelectedItems));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = selectedItems.findIndex(item => item.uniqueId === active.id);
      const overIndex = selectedItems.findIndex(item => item.uniqueId === over?.id);
      const newSelectedItems = arrayMove(selectedItems, activeIndex, overIndex);
      setSelectedItems(newSelectedItems);
      onChange(reconstructValue(newSelectedItems));
    }
  };

  const renderPartNumber = (item: SelectedItem) => {
    if (productRelationBrandCodeDelimiter && item.brandCode) {
      return `${item.partNumber} (${item.brandCode})`;
    }
    return item.partNumber;
  };

  const columns = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: () => <DragHandle disabled={disabled} icon={<MenuOutlined />} />,
    },
    {
      title: t('segments:customFields.productRelationPartNumber'),
      dataIndex: 'partNumber',
      key: 'partNumber',
      render: (_: any, record: SelectedItem) => renderPartNumber(record),
    },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_: any, record: SelectedItem & { index: number }) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.index)}
          disabled={disabled}
          size="small"
        />
      ),
    },
  ];

  return (
    <div className="product-relation-field-wrapper">
      <Select
        key={selectKey}
        className="mb-2"
        showSearch
        searchValue={searchValue}
        placeholder={t('segments:customFields.productRelationSearchPlaceholder')}
        filterOption={false}
        onSearch={handleSearch}
        onSelect={handleSelect}
        value={undefined}
        disabled={disabled}
        style={{ width: '100%' }}
        dropdownStyle={{ zIndex: 10000 }}
        popupClassName="product-relation-select-dropdown"
        options={searchedItems.map(item => ({
          key: item.id,
          value: item.id,
          label: `${item.part_number}${item.brand_code ? ` (${item.brand_code})` : ''}${
            item.short_name ? ` - ${item.short_name}` : ''
          }`,
        }))}
      />
      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={selectedItems.map(item => item.uniqueId)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            dataSource={selectedItems.map((item, index) => ({
              ...item,
              key: item.uniqueId,
              index,
            }))}
            columns={columns}
            pagination={false}
            size="small"
            components={{
              body: {
                row: DraggableRow,
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('segments:customFields.productRelationNoItems')}
                />
              ),
            }}
            style={{ maxHeight: 300, overflowY: 'auto' }}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ProductRelationField;
